import { useEffect } from "react"
import {
  useCreateIllustrationMappings,
  useIllustrationMappingInfo,
  useUpdateIllustrationMappings,
} from "../../../hooks/useIllustrationMappings"
import Loader from "../../../components/Loader"
import { Form, Formik } from "formik"
import { IllustrationMappingsFormSchema } from "../schema"
import TextField from "../../../components/TextField"
import DropDownField from "../../../components/DropDownField"
import ApiError from "../../../components/ApiError"

const CreateOrUpdateIllustrationMappingsFacility = (props) => {
  const {
    onClose,
    illustrationMappingId,
    page,
    limit,
    sortBy,
    filters,
    mappingType,
  } = props
  const { endpoint, idKey, singular, options } = mappingType

  const { data, isLoading, isError, error } = useIllustrationMappingInfo({
    endpoint,
    mappingId: illustrationMappingId,
  })
  const {
    mutateAsync: addIllustrationMappingFacility,
    isPending: isLoadingaddIllustrationMappingFacility,
  } = useCreateIllustrationMappings({ endpoint, page, limit, sortBy, filters })
  const {
    mutateAsync: updateIllustrationMappingFacility,
    isPending: isLoadingupdateIllustrationMappingFacility,
  } = useUpdateIllustrationMappings({
    endpoint,
    illustrationMappingId,
    page,
    limit,
    sortBy,
    filters,
  })

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [])

  const initialFormValues = {
    OriginalValue: data?.OriginalValue ?? "",
    MappedValue:
      data?.MappedValue?.split(",")
        .map((v) => v.trim())
        // Match stored values to the option's spelling, e.g. "anterior" -> "Anterior"
        .map(
          (v) => options.find((o) => o.toLowerCase() === v.toLowerCase()) ?? v
        )
        .map((v) => ({ value: v, label: v })) ?? [],
  }

  const handleSubmit = async (values) => {
    const payload = {
      originalValue: values.OriginalValue.trim(),
      mappedValue: values.MappedValue.map((v) => v.value)
        .sort()
        .join(","),
    }

    try {
      if (illustrationMappingId && data?.[idKey] === illustrationMappingId) {
        await updateIllustrationMappingFacility({
          illustrationMappingId,
          ...payload,
        })
      } else {
        await addIllustrationMappingFacility(payload)
      }
      onClose()
    } catch {
      // The mutation's onError shows a toast; keep the modal open so the user can retry.
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-5xl w-full">
        <div className="px-4 py-3 flex justify-between items-center">
          <p>
            <strong>
              {illustrationMappingId ? `Update ${singular}` : `Add ${singular}`}
            </strong>
          </p>
          <button
            className="text-gray-600 hover:text-gray-900 text-3xl ml-auto"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="max-h-96 p-4">
          {illustrationMappingId && isLoading ? (
            <div className="py-2">
              <Loader />
            </div>
          ) : illustrationMappingId && isError ? (
            <ApiError axiosError={error} />
          ) : (
            <div>
              <Formik
                enableReinitialize
                initialValues={initialFormValues}
                validationSchema={IllustrationMappingsFormSchema}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, values }) => (
                  <Form>
                    <div className="flex gap-6">
                      <div className="w-6/12">
                        <TextField
                          label="Text Input"
                          name="OriginalValue"
                          placeholder="Type text here..."
                        />
                      </div>
                    </div>

                    <div className="flex gap-6">
                      <div className="w-6/12">
                        <DropDownField
                          label="Mapped Value"
                          name="MappedValue"
                          options={options.map((mapping) => ({
                            value: mapping,
                            label: mapping,
                          }))}
                          placeholder="Select Value"
                          value={values.MappedValue}
                          onChange={(name, value) => setFieldValue(name, value)}
                        ></DropDownField>
                      </div>
                    </div>

                    <div className="flex justify-end mt-4">
                      <button
                        className="bg-gray-100 px-4 py-2 rounded-md mr-2 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:pointer-events-none"
                        disabled={
                          isLoadingaddIllustrationMappingFacility ||
                          isLoadingupdateIllustrationMappingFacility
                        }
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                      <button
                        className="bg-secondary text-white px-4 py-2 rounded-md mr-2 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:pointer-events-none"
                        disabled={
                          isLoadingaddIllustrationMappingFacility ||
                          isLoadingupdateIllustrationMappingFacility
                        }
                        type="submit"
                      >
                        {illustrationMappingId ? "Update" : "Add"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CreateOrUpdateIllustrationMappingsFacility
